/**
 * VendFactura SEO Configuration
 * Per-language meta tags, Open Graph, Twitter Cards, hreflang
 */

const DOMAIN = 'https://vendfactura.es';
const SUPPORTED_LANGS = ['es', 'en', 'fr', 'de', 'nl', 'hi', 'zh'];

const seoData = {
  es: {
    title: 'VendFactura - TPV y Facturación VeriFactu para Tiendas Españolas',
    description: 'Sistema TPV y facturación conforme a VeriFactu para tiendas y autónomos españoles. Escáner de código de barras, gestión de stock, facturas con QR. Desde €4.99/mes.',
    keywords: 'TPV, punto de venta, facturación, VeriFactu, AEAT, tienda, autónomo, España, factura electrónica, código de barras, gestión de stock, impresora térmica',
    ogTitle: 'VendFactura - Tu Tienda. Legal. Simple.',
    ogDescription: 'Sistema TPV y facturación VeriFactu para tiendas españolas. Cumple la normativa AEAT desde €4.99/mes.',
    locale: 'es_ES',
  },
  en: {
    title: 'VendFactura - POS System & VeriFactu Invoicing for Spanish Shops',
    description: 'Complete POS system and VeriFactu-compliant invoicing for Spanish shops and freelancers. Barcode scanner, stock management, QR invoices. From €4.99/mo.',
    keywords: 'POS, point of sale, invoicing, VeriFactu, AEAT, shop, freelancer, Spain, electronic invoice, barcode scanner, stock management, thermal printer',
    ogTitle: 'VendFactura - Your Shop. Compliant. Simple.',
    ogDescription: 'POS system and VeriFactu invoicing for Spanish shops. AEAT compliance from €4.99/mo.',
    locale: 'en_GB',
  },
  fr: {
    title: 'VendFactura - Caisse et Facturation VeriFactu pour Commerces Espagnols',
    description: 'Système de caisse complet et facturation conforme VeriFactu pour commerces et freelances espagnols. Scanner de codes-barres, gestion des stocks, factures QR. Dès €4.99/mois.',
    keywords: 'caisse, point de vente, facturation, VeriFactu, AEAT, commerce, freelance, Espagne, facture électronique, code-barres, gestion des stocks',
    ogTitle: 'VendFactura - Votre Boutique. Conforme. Simple.',
    ogDescription: 'Système de caisse et facturation VeriFactu pour commerces espagnols. Conformité AEAT dès €4.99/mois.',
    locale: 'fr_FR',
  },
  de: {
    title: 'VendFactura - Kassensystem & VeriFactu-Rechnungen für Spanische Geschäfte',
    description: 'Vollständiges Kassensystem und VeriFactu-konforme Rechnungsstellung für spanische Geschäfte und Freiberufler. Barcode-Scanner, Lagerverwaltung, QR-Rechnungen. Ab €4.99/Mo.',
    keywords: 'Kasse, Kassensystem, Rechnungsstellung, VeriFactu, AEAT, Geschäft, Freiberufler, Spanien, elektronische Rechnung, Barcode-Scanner, Lagerverwaltung',
    ogTitle: 'VendFactura - Ihr Laden. Konform. Einfach.',
    ogDescription: 'Kassensystem und VeriFactu-Rechnungen für spanische Geschäfte. AEAT-Konformität ab €4.99/Mo.',
    locale: 'de_DE',
  },
  nl: {
    title: 'VendFactura - Kassasysteem & VeriFactu-facturering voor Spaanse Winkels',
    description: 'Volledig kassasysteem en VeriFactu-conforme facturering voor Spaanse winkels en freelancers. Barcodescanner, voorraadbeheer, QR-facturen. Vanaf €4.99/mnd.',
    keywords: 'kassa, kassasysteem, facturering, VeriFactu, AEAT, winkel, freelancer, Spanje, elektronische factuur, barcodescanner, voorraadbeheer',
    ogTitle: 'VendFactura - Uw Winkel. Compliant. Simpel.',
    ogDescription: 'Kassasysteem en VeriFactu-facturering voor Spaanse winkels. AEAT-compliance vanaf €4.99/mnd.',
    locale: 'nl_BE',
  },
  hi: {
    title: 'VendFactura - स्पेनिश दुकानों के लिए POS सिस्टम और VeriFactu इनवॉइसिंग',
    description: 'स्पेनिश दुकानों और फ्रीलांसरों के लिए पूर्ण POS सिस्टम और VeriFactu-अनुपालित इनवॉइसिंग। बारकोड स्कैनर, स्टॉक प्रबंधन, QR इनवॉइस। €4.99/माह से।',
    keywords: 'POS, बिक्री बिंदु, इनवॉइसिंग, VeriFactu, AEAT, दुकान, फ्रीलांसर, स्पेन, इलेक्ट्रॉनिक इनवॉइस, बारकोड स्कैनर, स्टॉक प्रबंधन',
    ogTitle: 'VendFactura - आपकी दुकान। अनुपालित। सरल।',
    ogDescription: 'स्पेनिश दुकानों के लिए POS सिस्टम और VeriFactu इनवॉइसिंग। €4.99/माह से AEAT अनुपालन।',
    locale: 'hi_IN',
  },
  zh: {
    title: 'VendFactura - 西班牙商店POS系统和VeriFactu发票',
    description: '面向西班牙商店和自由职业者的完整POS系统和VeriFactu合规发票。条码扫描、库存管理、QR发票。€4.99/月起。',
    keywords: 'POS, 收银台, 发票, VeriFactu, AEAT, 商店, 自由职业者, 西班牙, 电子发票, 条码扫描, 库存管理',
    ogTitle: 'VendFactura - 您的商店。合规。简单。',
    ogDescription: '面向西班牙商店的POS系统和VeriFactu发票。€4.99/月起实现AEAT合规。',
    locale: 'zh_CN',
  },
};

/**
 * JSON-LD structured data for SoftwareApplication
 */
const getStructuredData = (lang) => {
  const data = seoData[lang] || seoData.es;
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'VendFactura',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `${DOMAIN}/${lang}`,
    description: data.description,
    offers: [
      {
        '@type': 'Offer',
        name: lang === 'es' ? 'Plan Facturas' : 'Invoices Plan',
        price: '4.99',
        priceCurrency: 'EUR',
        priceValidUntil: '2026-12-31',
      },
      {
        '@type': 'Offer',
        name: lang === 'es' ? 'Plan TPV Tienda' : 'Shop POS Plan',
        price: '19.99',
        priceCurrency: 'EUR',
        priceValidUntil: '2026-12-31',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '523',
    },
    publisher: {
      '@type': 'Organization',
      name: 'VendFactura',
      url: DOMAIN,
      logo: `${DOMAIN}/logo.png`,
    },
  };
};

/**
 * Generate hreflang links for all supported languages
 */
const getHreflangLinks = (currentPath = '') => {
  const path = currentPath.replace(/^\/(es|en|fr|de|nl|hi|zh)\/?/, '/');
  return SUPPORTED_LANGS.map(lang => ({
    rel: 'alternate',
    hrefLang: lang === 'es' ? 'es' : lang === 'zh' ? 'zh-Hans' : lang,
    href: `${DOMAIN}/${lang}${path}`,
  })).concat([{
    rel: 'alternate',
    hrefLang: 'x-default',
    href: `${DOMAIN}/es${path}`,
  }]);
};

export { seoData, getStructuredData, getHreflangLinks, DOMAIN, SUPPORTED_LANGS };
